/**
 * Fenda Studio - Search JavaScript
 * Handles predictive search functionality
 */

class PredictiveSearch {
  constructor() {
    this.searchInputs = document.querySelectorAll('[data-search-input]');
    this.resultsContainers = document.querySelectorAll('[data-search-results]');
    this.abortController = null;
    
    this.init();
  }

  init() {
    this.searchInputs.forEach(input => {
      input.addEventListener('input', this.debounce((e) => {
        this.performSearch(e.target.value);
      }, 300));
      
      input.addEventListener('focus', (e) => {
        if (e.target.value.length >= 2) {
          this.performSearch(e.target.value);
        }
      });
    });
  }

  async performSearch(query) {
    // Cancel previous request
    if (this.abortController) {
      this.abortController.abort();
    }
    
    if (query.length < 2) {
      this.clearResults();
      return;
    }
    
    this.abortController = new AbortController();
    
    try {
      const response = await fetch(
        `${window.Shopify.routes.root}search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=6&resources[options][unavailable_products]=hide`,
        { signal: this.abortController.signal }
      );
      
      if (!response.ok) throw new Error('Search failed');
      
      const data = await response.json();
      this.renderResults(data.resources.results.products, query);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error('Search error:', error);
      }
    }
  }

  renderResults(products, query) {
    this.resultsContainers.forEach(container => {
      if (!products || products.length === 0) {
        container.innerHTML = `
          <div class="search-no-results">
            <p class="text-body">No products found for "${query}"</p>
            <a href="${window.Shopify.routes.root}collections/all" class="text-caption link-hover">
              Browse all products →
            </a>
          </div>
        `;
        return;
      }
      
      container.innerHTML = `
        <div class="search-results-list">
          ${products.map(product => this.renderProductResult(product)).join('')}
        </div>
        <a href="${window.Shopify.routes.root}search?q=${encodeURIComponent(query)}&type=product" class="search-view-all text-caption link-hover">
          View all results →
        </a>
      `;
      this.hydratePredictivePrices(container, products);
    });
  }

  renderProductResult(product) {
    const imageUrl = product.featured_image ? 
      `${product.featured_image.url}&width=120&height=160&crop=center` : 
      null;
    
    return `
      <a href="${product.url}" class="search-result-item">
        ${imageUrl ? 
          `<img src="${imageUrl}" alt="${product.title}" class="search-result-item__image" loading="lazy">` : 
          `<div class="search-result-item__placeholder"></div>`
        }
        <div class="search-result-item__content">
          ${product.type ? `<span class="search-result-item__type text-caption">${product.type.toLowerCase()}</span>` : ''}
          <h4 class="search-result-item__title">${product.title.toLowerCase()}</h4>
          <div class="search-result-item__price" data-price-url="${product.url}" data-raw-price="${product.price || ''}">${this.safeFormatPredictivePrice(product)}</div>
        </div>
      </a>
    `;
  }

  clearResults() {
    this.resultsContainers.forEach(container => {
      container.innerHTML = '';
    });
  }

  formatMoney(cents) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: window.Shopify?.currency?.active || 'EUR'
    }).format(cents / 100);
  }

  safeFormatPredictivePrice(product) {
    const raw = typeof product.price === 'number' ? product.price : parseFloat(product.price || '0');
    if (Number.isFinite(raw) && raw >= 1000) {
      return this.formatMoney(raw);
    }
    if (Number.isFinite(raw) && raw > 0 && raw < 1000) {
      return this.formatMoney(Math.round(raw * 100));
    }
    return '';
  }

  async hydratePredictivePrices(container, products) {
    if (!this._priceCache) this._priceCache = new Map();
    const nodes = Array.from(container.querySelectorAll('[data-price-url]'));
    if (!nodes.length) return;
    await Promise.all(nodes.map(async el => {
      const url = el.getAttribute('data-price-url');
      if (!url) return;
      try {
        if (this._priceCache.has(url)) {
          el.textContent = this.formatMoney(this._priceCache.get(url));
          return;
        }
        const res = await fetch(url + '.js');
        const data = await res.json();
        const cents = data?.variants && data.variants[0]?.price ? parseInt(data.variants[0].price, 10) : 0;
        this._priceCache.set(url, cents);
        el.textContent = this.formatMoney(cents);
      } catch (_) { /* no-op */ }
    }));
  }

  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }
}

// Initialize search
document.addEventListener('DOMContentLoaded', () => {
  window.predictiveSearch = new PredictiveSearch();
});
